import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";

function StatTable({ title, head, rows, cols }) {
  return (
    <div className="mb-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-1.5">{title}</p>
      <div className="overflow-x-auto rounded-md border border-white/10">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/[0.03] text-white/45">
              <th className="text-left font-bold uppercase tracking-wider px-3 py-1.5">{head}</th>
              {cols.map((c) => <th key={c} className="text-right font-bold uppercase tracking-wider px-2 py-1.5">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.name || i} className="border-t border-white/5">
                <td className="text-left px-3 py-1.5 text-white/85 truncate max-w-[140px]">{r.name}</td>
                {r.vals.map((v, j) => <td key={cols[j] || j} className="text-right px-2 py-1.5 font-num text-white/70">{v ?? "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BoxScore({ gameId, game }) {
  const [box, setBox] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/games/${gameId}/boxscore`);
      setBox(r.data);
    } catch (err) {
      captureError("boxscore load failed:", err);
    } finally {
      setLoaded(true);
    }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (game.status !== "LIVE") return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [game.status, load]);

  if (loaded && (!box || !box.available)) {
    return <div className="p-6 rounded-md bg-[#121212] border border-white/10 text-white/50 text-sm" data-testid="boxscore-unavailable">Box score not available yet.</div>;
  }
  if (!box) return <div className="p-6 text-white/50 text-sm" data-testid="boxscore-loading">Loading box score…</div>;

  const sides = [
    { key: "away", abbr: game.away_team_abbr, label: game.away_team },
    { key: "home", abbr: game.home_team_abbr, label: game.home_team },
  ];

  return (
    <div data-testid="boxscore">
      {/* Line score */}
      <div className="overflow-x-auto rounded-md border border-white/10 mb-5">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/[0.03] text-white/45">
              <th className="text-left font-bold uppercase tracking-wider px-3 py-2">Team</th>
              {box.innings.map((inn) => <th key={inn.num} className="px-2 py-2 font-num">{inn.num}</th>)}
              <th className="px-2 py-2 font-bold text-white/70">R</th>
              <th className="px-2 py-2 font-bold text-white/70">H</th>
              <th className="px-2 py-2 font-bold text-white/70">E</th>
            </tr>
          </thead>
          <tbody>
            {sides.map((s) => (
              <tr key={s.key} className="border-t border-white/5">
                <td className="text-left px-3 py-2 font-display font-bold uppercase tracking-tight">{s.abbr}</td>
                {box.innings.map((inn) => <td key={inn.num} className="text-center px-2 py-2 font-num text-white/60">{inn[s.key] ?? "·"}</td>)}
                <td className="text-center px-2 py-2 font-num font-black">{box.totals[s.key]?.runs ?? 0}</td>
                <td className="text-center px-2 py-2 font-num text-white/70">{box.totals[s.key]?.hits ?? 0}</td>
                <td className="text-center px-2 py-2 font-num text-white/70">{box.totals[s.key]?.errors ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {sides.map((s) => (
          <div key={s.key}>
            <h4 className="font-display font-bold text-base uppercase tracking-tight mb-2">{s.label}</h4>
            <StatTable
              title="Batting" head="Batter" cols={["AB", "R", "H", "RBI", "AVG"]}
              rows={(box[s.key]?.batters || []).map((b) => ({ name: b.name, vals: [b.ab, b.r, b.h, b.rbi, b.avg] }))}
            />
            <StatTable
              title="Pitching" head="Pitcher" cols={["IP", "H", "ER", "K", "ERA"]}
              rows={(box[s.key]?.pitchers || []).map((p) => ({ name: p.name, vals: [p.ip, p.h, p.er, p.k, p.era] }))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
